import { createRoot } from "react-dom/client";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";

const SENTRY_DSN =
  "https://dbec50db5a736818bce9fd9899b3a0e8@o4511719357808640.ingest.us.sentry.io/4511781365153792";

/** Third-party scripts we don't control — their errors are noise, not our bugs. */
const THIRD_PARTY_PATTERNS = [
  /tawk\.to/i,
  /twk-chunk/i,
  /twk-vendor/i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /gtag\/js/i,
];

const isThirdPartyNoise = (event: Sentry.ErrorEvent): boolean => {
  const frames =
    event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];

  return frames.some((frame) =>
    THIRD_PARTY_PATTERNS.some((pattern) =>
      pattern.test(frame.filename ?? "") || pattern.test(frame.abs_path ?? "")
    )
  );
};

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.2,
    tracePropagationTargets: [/^https:\/\/mountingstandoffs\.com/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      /\$_Tawk/i,
      /Tawk_API/i,
      /i18next is not a function/i,
      /twk-/i,
    ],
    denyUrls: [
      /embed\.tawk\.to/i,
      /tawk\.to/i,
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
    ],
    beforeSend(event) {
      return isThirdPartyNoise(event) ? null : event;
    },
  });
}


createRoot(document.getElementById("root")!).render(<App />);
